import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Textarea } from 'app/components/ui/textarea';
import { Switch } from 'app/components/ui/switch';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import { Separator } from 'app/components/ui/separator';
import {
  Users,
  Shield,
  AlertCircle,
  ExternalLink,
  CheckCircle,
  Info,
  Eye,
  EyeOff,
  Copy,
  Save,
  AlertTriangle,
} from 'lucide-react';
import { useSettingsSlice } from '../slice';

interface SocialAuthSettingsProps {
  form: UseFormReturn<any>;
}

// Helper function to create payload with is_public: 0 for all keys
const createSocialAuthSettingsPayload = (formData: any) => {
  const payload: Record<string, { option_value: any; is_public: number }> = {};

  // Add is_public: 0 for all social auth settings (sensitive data)
  const settingsKeys = [
    'social_auth_google_enabled',
    'social_auth_google_client_id',
    'social_auth_google_client_secret',
    'social_auth_google_redirect_uri',
    'social_auth_facebook_enabled',
    'social_auth_facebook_app_id',
    'social_auth_facebook_app_secret',
    'social_auth_facebook_redirect_uri',
    'social_auth_github_enabled',
    'social_auth_github_client_id',
    'social_auth_github_client_secret',
    'social_auth_github_redirect_uri',
  ];

  settingsKeys.forEach(key => {
    if (formData[key] !== undefined) {
      payload[key] = {
        option_value: formData[key],
        is_public: 0,
      };
    }
  });

  return payload;
};

export default function SocialAuthSettings({ form }: SocialAuthSettingsProps) {
  const [showSecrets, setShowSecrets] = React.useState<Record<string, boolean>>(
    {},
  );
  const [updateStatus, setUpdateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const { useSaveSiteSettingsMutation } = useSettingsSlice();
  const [saveSiteSettings, { isLoading: isUpdating }] =
    useSaveSiteSettingsMutation();

  const toggleSecret = (fieldName: string) => {
    setShowSecrets(prev => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Function to handle settings update with is_public: 0 for all keys
  const handleUpdateSettings = async () => {
    try {
      setUpdateStatus('idle');
      const formData = form.getValues();
      const payload = createSocialAuthSettingsPayload(formData);

      console.log('Social auth settings payload with is_public: 0:', payload);

      // Use the Redux mutation to save settings
      const result = await saveSiteSettings(payload).unwrap();
      console.log('Social auth settings updated successfully:', result);

      setUpdateStatus('success');
      return result;
    } catch (error) {
      console.error('Error updating social auth settings:', error);
      setUpdateStatus('error');
      throw error;
    }
  };

  return (
    <>
      {/* Google Authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Google Authentication
          </CardTitle>
          <CardDescription>
            Configure Google OAuth 2.0 for user authentication.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="social_auth_google_enabled"
              checked={form.watch('social_auth_google_enabled')}
              onCheckedChange={value =>
                form.setValue('social_auth_google_enabled', value)
              }
            />
            <Label htmlFor="social_auth_google_enabled">
              Enable Google Sign-In
            </Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="social_auth_google_client_id">Client ID</Label>
              <Input
                id="social_auth_google_client_id"
                {...form.register('social_auth_google_client_id')}
                placeholder="123456789-abcdefghijklmnop.apps.googleusercontent.com"
              />
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  Format: numbers-letters.apps.googleusercontent.com
                </p>
                <a
                  href="https://console.cloud.google.com/apis/credentials"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                >
                  Get Key <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="social_auth_google_client_secret">
                Client Secret
              </Label>
              <div className="relative">
                <Input
                  id="social_auth_google_client_secret"
                  type={showSecrets['google_secret'] ? 'text' : 'password'}
                  {...form.register('social_auth_google_client_secret')}
                  placeholder="GOCSPX-abcdefghijklmnopqrstuvwxyz123456"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-6 w-6 p-0"
                  onClick={() => toggleSecret('google_secret')}
                >
                  {showSecrets['google_secret'] ? (
                    <EyeOff className="w-3 h-3" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Format: GOCSPX-followed by 24 characters
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="social_auth_google_redirect_uri">
              Redirect URI
            </Label>
            <Input
              id="social_auth_google_redirect_uri"
              {...form.register('social_auth_google_redirect_uri')}
              value={`https://${form.watch('site_url')}/auth/callback/google`}
              disabled={true}
            />
            <div className="flex justify-between items-center">
              <p className="text-xs text-muted-foreground">
                Must match exactly what's configured in Google Console
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  copyToClipboard(
                    `https://${form.watch('site_url')}/auth/callback/google`,
                  )
                }
                className="text-xs"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-600" />
              <div>
                <p className="text-sm text-blue-800 font-medium mb-2">
                  How to get Google OAuth credentials:
                </p>
                <ol className="text-sm text-blue-700 space-y-1">
                  <li>
                    1. Go to{' '}
                    <a
                      href="https://console.cloud.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Google Cloud Console
                    </a>
                  </li>
                  <li>2. Create a new project or select existing one</li>
                  <li>3. Enable Google+ API and Google OAuth2 API</li>
                  <li>
                    4. Go to Credentials → Create Credentials → OAuth 2.0 Client
                    ID
                  </li>
                  <li>5. Set Application Type to "Web application"</li>
                  <li>6. Add your redirect URI to Authorized redirect URIs</li>
                  <li>7. Copy Client ID and Client Secret</li>
                </ol>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Facebook Authentication */}
      <Card className="hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Facebook Authentication
          </CardTitle>
          <CardDescription>
            Configure Facebook Login for user authentication.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="social_auth_facebook_enabled"
              checked={form.watch('social_auth_facebook_enabled') == 1}
              onCheckedChange={value =>
                form.setValue('social_auth_facebook_enabled', value)
              }
            />
            <Label htmlFor="social_auth_facebook_enabled">
              Enable Facebook Login
            </Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="social_auth_facebook_app_id">App ID</Label>
              <Input
                id="social_auth_facebook_app_id"
                {...form.register('social_auth_facebook_app_id')}
                placeholder="123456789012345"
              />
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  Format: 15-digit number
                </p>
                <a
                  href="https://developers.facebook.com/apps/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                >
                  Get Key <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="social_auth_facebook_app_secret">
                App Secret
              </Label>
              <div className="relative">
                <Input
                  id="social_auth_facebook_app_secret"
                  type={showSecrets['facebook_secret'] ? 'text' : 'password'}
                  {...form.register('social_auth_facebook_app_secret')}
                  placeholder="abcdef123456789abcdef123456789ab"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-6 w-6 p-0"
                  onClick={() => toggleSecret('facebook_secret')}
                >
                  {showSecrets['facebook_secret'] ? (
                    <EyeOff className="w-3 h-3" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Format: 32-character hexadecimal string
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="social_auth_facebook_redirect_uri">
              Redirect URI
            </Label>
            <Input
              id="social_auth_facebook_redirect_uri"
              {...form.register('social_auth_facebook_redirect_uri')}
              placeholder="https://mobilefirmware.com/auth/facebook/callback"
            />
            <div className="flex justify-between items-center">
              <p className="text-xs text-muted-foreground">
                Must match exactly what's configured in Facebook App
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  copyToClipboard(
                    'https://mobilefirmware.com/auth/facebook/callback',
                  )
                }
                className="text-xs"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-600" />
              <div>
                <p className="text-sm text-blue-800 font-medium mb-2">
                  How to get Facebook App credentials:
                </p>
                <ol className="text-sm text-blue-700 space-y-1">
                  <li>
                    1. Go to{' '}
                    <a
                      href="https://developers.facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Facebook Developers
                    </a>
                  </li>
                  <li>2. Create a new app or select existing one</li>
                  <li>3. Add Facebook Login product to your app</li>
                  <li>4. Go to Settings → Basic to find App ID</li>
                  <li>
                    5. Go to Settings → Basic → App Secret to reveal secret
                  </li>
                  <li>6. Add your redirect URI to Valid OAuth Redirect URIs</li>
                  <li>7. Copy App ID and App Secret</li>
                </ol>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* GitHub Authentication */}
      <Card className="hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            GitHub Authentication
          </CardTitle>
          <CardDescription>
            Configure GitHub OAuth for developer authentication.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="social_auth_github_enabled"
              checked={form.watch('social_auth_github_enabled')}
              onCheckedChange={value =>
                form.setValue('social_auth_github_enabled', value)
              }
            />
            <Label htmlFor="social_auth_github_enabled">
              Enable GitHub Sign-In
            </Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="social_auth_github_client_id">Client ID</Label>
              <Input
                id="social_auth_github_client_id"
                {...form.register('social_auth_github_client_id')}
                placeholder="abc123def456ghi789"
              />
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  Format: alphanumeric string
                </p>
                <a
                  href="https://github.com/settings/developers"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                >
                  Get Key <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="social_auth_github_client_secret">
                Client Secret
              </Label>
              <div className="relative">
                <Input
                  id="social_auth_github_client_secret"
                  type={showSecrets['github_secret'] ? 'text' : 'password'}
                  {...form.register('social_auth_github_client_secret')}
                  placeholder="jkl012mno345pqr678stu901vwx234"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-6 w-6 p-0"
                  onClick={() => toggleSecret('github_secret')}
                >
                  {showSecrets['github_secret'] ? (
                    <EyeOff className="w-3 h-3" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Format: alphanumeric string (40 characters)
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="social_auth_github_redirect_uri">
              Redirect URI
            </Label>
            <Input
              id="social_auth_github_redirect_uri"
              {...form.register('social_auth_github_redirect_uri')}
              placeholder="https://mobilefirmware.com/auth/github/callback"
            />
            <div className="flex justify-between items-center">
              <p className="text-xs text-muted-foreground">
                Must match exactly what's configured in GitHub OAuth App
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  copyToClipboard(
                    'https://mobilefirmware.com/auth/github/callback',
                  )
                }
                className="text-xs"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-600" />
              <div>
                <p className="text-sm text-blue-800 font-medium mb-2">
                  How to get GitHub OAuth credentials:
                </p>
                <ol className="text-sm text-blue-700 space-y-1">
                  <li>
                    1. Go to{' '}
                    <a
                      href="https://github.com/settings/developers"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      GitHub Developer Settings
                    </a>
                  </li>
                  <li>2. Click "New OAuth App"</li>
                  <li>3. Fill in application details</li>
                  <li>
                    4. Set Authorization callback URL to your redirect URI
                  </li>
                  <li>5. Register application</li>
                  <li>6. Copy Client ID and generate Client Secret</li>
                  <li>7. Note: Client Secret can only be viewed once</li>
                </ol>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Security Notice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-sm text-red-800 font-medium">
                  Important Security Information
                </p>
                <ul className="text-sm text-red-700 mt-2 space-y-1">
                  <li>
                    • Keep your client secrets secure and never expose them in
                    client-side code
                  </li>
                  <li>
                    • Use environment variables for sensitive configuration
                  </li>
                  <li>• Regularly rotate your OAuth credentials</li>
                  <li>
                    • Ensure redirect URIs are properly configured in your OAuth
                    apps
                  </li>
                  <li>
                    • Test authentication flows in development before production
                  </li>
                  <li>• Never commit secrets to version control</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fixed Save Button - Bottom Right */}
      <div className="fixed bottom-14 right-6 z-50">
        <div className="flex flex-col items-end gap-3">
          {/* Status Feedback */}
          {updateStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Social auth settings saved successfully!
                </span>
              </div>
            </div>
          )}

          {updateStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Failed to save social auth settings. Please try again.
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleUpdateSettings}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
            variant="default"
            disabled={isUpdating}
            size="lg"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
